import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAppLocale } from '../../hooks/useAppLocale';
import DoneIcon from '@mui/icons-material/Done';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ErrorIcon from '@mui/icons-material/Error';
import SendIcon from '@mui/icons-material/Send';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useTranslate } from 'react-admin';
import type { MessageStatus } from '../../types/chat.types';
import { API_URL } from '../../httpClient';

interface MessageStatusHistoryEntry {
  id: number;
  messageId: number;
  status: MessageStatus;
  occurredAt: string;
  createdAt: string;
  metadata: { error?: string } | null;
}

interface MessageStatusHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  messageId: number;
}

const STATUS_CONFIG: Record<
  MessageStatus,
  {
    icon: React.ReactNode;
    color: string;
    labelKey: string;
  }
> = {
  PROCESSING: {
    icon: <AccessTimeIcon />,
    color: '#9e9e9e',
    labelKey: 'resources.message.status.processing',
  },
  PENDING: {
    icon: <SendIcon />,
    color: '#9e9e9e',
    labelKey: 'resources.message.status.pending',
  },
  SENT: {
    icon: <DoneIcon />,
    color: '#2196f3',
    labelKey: 'resources.message.status.sent',
  },
  RECEIVED: {
    icon: <DoneAllIcon />,
    color: '#4caf50',
    labelKey: 'resources.message.status.received',
  },
  READ: {
    icon: <DoneAllIcon />,
    color: '#53bdeb',
    labelKey: 'resources.message.status.read',
  },
  PLAYED: {
    icon: <PlayArrowIcon />,
    color: '#53bdeb',
    labelKey: 'resources.message.status.played',
  },
  FAILED: {
    icon: <ErrorIcon />,
    color: '#f44336',
    labelKey: 'resources.message.status.failed',
  },
};

export function MessageStatusHistoryDialog({
  open,
  onClose,
  messageId,
}: MessageStatusHistoryDialogProps) {
  const translate = useTranslate();
  const appLocale = useAppLocale();
  const [history, setHistory] = useState<MessageStatusHistoryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !messageId) return;

    const fetchHistory = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem('token');
        const tenantUuid = localStorage.getItem('tenantUuid');

        if (!token || !tenantUuid) {
          throw new Error('Not authenticated');
        }

        const response = await fetch(`${API_URL}/message/${messageId}/status-history`, {
          headers: {
            'x-tenant-user-auth': token,
            'x-tenant-uuid': tenantUuid,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch status history: ${response.statusText}`);
        }

        const result = await response.json();
        setHistory(result.data || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchHistory();
  }, [open, messageId]);

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString(appLocale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }),
      time: date.toLocaleTimeString(appLocale, {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }),
    };
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        {translate('resources.message.statusHistory.title')}
        <IconButton onClick={onClose} size="small" sx={{ ml: 2 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        {isLoading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box py={2}>
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          </Box>
        )}

        {!isLoading && !error && history.length === 0 && (
          <Box py={2}>
            <Typography color="textSecondary" variant="body2">
              {translate('resources.message.statusHistory.empty')}
            </Typography>
          </Box>
        )}

        {!isLoading && !error && history.length > 0 && (
          <List dense sx={{ py: 0 }}>
            {history.map((entry, index) => {
              const config = STATUS_CONFIG[entry.status] || STATUS_CONFIG.PENDING;
              const { date, time } = formatDateTime(entry.occurredAt);
              const isLast = index === history.length - 1;

              return (
                <Box key={entry.id}>
                  <ListItem
                    sx={{
                      py: 1.5,
                      px: 0,
                      position: 'relative',
                      '&::before': !isLast ? {
                        content: '""',
                        position: 'absolute',
                        left: 18,
                        top: 44,
                        bottom: -12,
                        width: 2,
                        bgcolor: 'divider',
                      } : undefined,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 44 }}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: isLast ? config.color : 'transparent',
                          border: isLast ? 'none' : `2px solid ${config.color}`,
                          color: isLast ? 'white' : config.color,
                          '& svg': {
                            fontSize: 20,
                          },
                        }}
                      >
                        {config.icon}
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2" fontWeight={isLast ? 600 : 400}>
                          {translate(config.labelKey)}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography component="span" variant="body2" color="textSecondary" sx={{ display: 'block' }}>
                            {date} • {time}
                          </Typography>
                          {entry.metadata?.error && (
                            <Typography component="span" variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                              {entry.metadata.error}
                            </Typography>
                          )}
                        </>
                      }
                      secondaryTypographyProps={{ component: 'div' }}
                    />
                  </ListItem>
                  {!isLast && <Divider variant="inset" component="li" sx={{ ml: 5.5 }} />}
                </Box>
              );
            })}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
}
